/* eslint-disable jsx-a11y/label-has-for */
import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { unitValueMap } from '@bingads-webui-campaign/reach-estimate-control';

/**
 * @const {Object} COLUMN_FORMATS The different formats supported by ProjectionGridBaseColumn
 * @property {string} PERCENTAGE_VALUE value: 'percentage-value'
 *    Formats a float as percentage. E.g.: 42.07 => 42.07%, 0.15 => 0.15%
 * @property {string} DECIMAL_Original: value: 'decimal-original'
 *    A number with at most two decimal points.
 * @property {string} DECIMAL: value: 'decimal'
 *    A number with two decimal points.
 */
const COLUMN_FORMATS = {
  PERCENTAGE_VALUE: 'percentage-value',
  DECIMAL_Original: 'decimal-original',
  DECIMAL: 'decimal',
};

const getEstimateDataConfig = (pricingModel, isVideo) => _.compact([
  {
    name: 'impressions',
    i18nLabel: 'EstimatedImpressions',
    helpTopic: 'pop_BA_CampaignCreate_AudAds_EstImpressions',
    ApiDataName: 'EstImpression',
    format: COLUMN_FORMATS.DECIMAL_Original,
  },
  isVideo && {
    name: 'views',
    i18nLabel: 'EstimatedViews',
    helpTopic: 'pop_BA_CampaignCreate_AudAds_EstViews',
    ApiDataName: 'EstView',
    format: COLUMN_FORMATS.DECIMAL_Original,
  },
  {
    name: 'clicks',
    i18nLabel: 'EstimatedClicks',
    helpTopic: 'pop_BA_CampaignCreate_AudAds_EstClicks',
    ApiDataName: 'EstClick',
    format: COLUMN_FORMATS.DECIMAL_Original,
  },
  {
    name: 'CTR',
    i18nLabel: 'EstimatedCTR',
    helpTopic: 'pop_BA_CampaignCreate_AudAds_EstCTR',
    ApiDataName: 'EstCTR',
    format: COLUMN_FORMATS.PERCENTAGE_VALUE,
  },
  {
    name: `avg${pricingModel}`,
    i18nLabel: `EstimatedAvg${pricingModel}`,
    helpTopic: `pop_BA_CampaignCreate_AudAds_Est${pricingModel}`,
    ApiDataName: 'EstCPC',
    format: COLUMN_FORMATS.DECIMAL,
  },
  {
    name: 'spend',
    i18nLabel: 'EstimatedSpend',
    helpTopic: 'pop_BA_CampaignCreate_AudAds_EstSpend',
    ApiDataName: 'EstSpend',
    format: COLUMN_FORMATS.DECIMAL,
  },
]);

const getFormatter = (i18n, format, value) => {
  let formatter = _.identity;

  switch (format) {
    case COLUMN_FORMATS.PERCENTAGE_VALUE:
      formatter = _.compose(i18n.percentToFixedFormatter({
        digits: 2,
      }), number => number / 100);
      break;
    case COLUMN_FORMATS.DECIMAL_Original:
      formatter = i18n.decimalFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
      break;
    case COLUMN_FORMATS.DECIMAL:
      formatter = i18n.decimalFormatter({
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      break;
    default:
      break;
  }

  return _.isNull(value) || _.isUndefined(value) ?
    '' : formatter(value);
};

const getValue = (obj, i18n, format) => {
  let result;
  let unitShort;

  if (_.isObject(obj)) {
    result = obj.Value;
    unitShort = unitValueMap[obj.Unit].unit;
  } else {
    result = obj;
    unitShort = '';
  }
  return `${getFormatter(i18n, format, result)}${unitShort}`;
};

export const getRange = (data, i18n, format) => {
  if (data === null || data === undefined) {
    return ' - ';
  }
  const { High, Low } = data;
  if ((_.isObject(High) && High.Value === 0) || (_.isNumber(High) && High === 0)) {
    return `${getValue(High, i18n, format)}`;
  }
  return `${getValue(Low, i18n, format)} - ${getValue(High, i18n, format)}`;
};

export function KPIEstimateReact(props) {
  const { pricingModel, isVideo } = props;
  const learnMore = props.i18n.getString('LearnMoreLinkText');
  const estimateDataConfig = getEstimateDataConfig(pricingModel, isVideo);

  return (
    <div className="panel panel-default">
      <div className="panel-heading" />
      {props.reqStatus ?
        <div className="panel-body">
          {estimateDataConfig.map(item => (
            <div className="form-group" key={item.name}>
              <div className="col-sm-3 control-label">
                <label>{props.i18n.getString(item.i18nLabel)}</label>
                <HelpPopup
                  topic={item.helpTopic}
                  aria-label={`${learnMore} ${props.i18n.getString(item.i18nLabel)}`}
                />
              </div>
              <div className={`col-sm-4 estimated-${item.name} form-control-static`} x-ms-format-detection="none">
                {getRange(
                  props[item.ApiDataName] || {},
                  props.i18n,
                  item.format
                )}
              </div>
            </div>))}
        </div> :
        <div className="alert alert-warning">
          {props.i18n.getString('Audience_Campaign_KPIEstimation_RequestError')}
        </div>
      }
    </div>
  );
}

KPIEstimateReact.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    percentToFixedFormatter: PropTypes.func.isRequired,
    decimalFormatter: PropTypes.func.isRequired,
  }).isRequired,
  reqStatus: PropTypes.bool.isRequired,
  pricingModel: PropTypes.string.isRequired,
  isVideo: PropTypes.bool,
};

KPIEstimateReact.defaultProps = {
  isVideo: false,
};
